'use client'

import { Box, IconButton, Stack, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import Rating from './Rating'
import Description from './Description'
import Nutrients from './Nutrients'
import { shallowEqual, useSelector } from 'react-redux'
import { IRedux } from '@/app/[locale]/_store/types'
import { useQuery } from '@tanstack/react-query'
import productService from '@/app/[locale]/_service/product.service'
import { useDynamicLanguage } from '@/app/[locale]/_hooks/useShipper'
import Details from './Details'
import Combo from './Combo'
import Slider from './Slider'
import Ingredients from './Ingredients'
import { Alert } from '@/components/ui/alert'
import { useCustomLocale } from '@/app/[locale]/_hooks/useCustomLocale'
import { BackIcon } from '@/app/[locale]/_assets/icon-components'
import { usePathname, useRouter } from '@/i18n/navigation'
import Script from 'next/script'
import {
  resolvePlatformId,
  resolveSourceId,
} from '@/app/[locale]/_utils/aggregator-context'
import {
  getMiniAppCrmType,
  getMiniAppProvider,
} from '@/app/[locale]/_utils/mini-app'
import { IProduct } from '@/app/[locale]/_types'

const ProductMobile = ({
  data: dataProp,
  slug,
  onSuccess,
  onRedirectToCart,
}: {
  data?: IProduct
  slug?: string
  onSuccess?: () => void
  onRedirectToCart?: () => void
}) => {
  const router = useRouter()
  const pathname = usePathname()
  const currentLocale = useCustomLocale()

  const {
    auth,
    branch,
    session,
    hide_product_reviews,
    shipper_id,
    platform_id,
    source_id,
  } = useSelector((state: IRedux) => {
    const resolvedSourceId = resolveSourceId(state.session, state.auth)
    return {
      auth: state.auth,
      branch: state.common.branch,
      session: state.session,
      hide_product_reviews: state.settings.data?.data?.hide_product_reviews,
      shipper_id: state.settings?.data?.shipper_id,
      source_id: resolvedSourceId,
      platform_id: resolvePlatformId(state.session, resolvedSourceId),
    }
  }, shallowEqual)

  const user = auth.user

  const { is_hall, menu_id, order_type } = session
  const isHall = is_hall === 'true'

  const selectedMenuId =
    isHall || pathname.endsWith('/menu')
      ? menu_id !== 'null'
        ? menu_id
        : ''
      : branch?.menu_id || ''

  const productParams = {
    only_delivery: String(order_type?.type === 'delivery'),
    only_self_pickup: String(order_type?.type === 'self-pickup'),
    branch_id: branch?.id || '',
    menu_id: selectedMenuId,
    client_id: user?.id || '',
    with_discounts: 'true',
    fields: 'discounts',
    aggregator_id: source_id,
    order_source: getMiniAppCrmType(getMiniAppProvider(null)),
    order_type_id: order_type?.id,
  }

  const productId = dataProp?.id || slug || ''

  const { useDynamicLanguage: dynamic, isShipperResolved } =
    useDynamicLanguage()

  const { data: fetchedData } = useQuery({
    queryKey: ['product-by-id', productId, productParams, dynamic],
    queryFn: () => {
      return productService
        .getById(productId, productParams, shipper_id, platform_id, dynamic)
        .then((res) => res?.data)
    },
    enabled:
      !dataProp &&
      Boolean(productId) &&
      Boolean(shipper_id) &&
      isShipperResolved,
  })

  const data = dataProp ?? fetchedData

  const jsonLd = useMemo(() => {
    if (!data) return null

    const baseUrl = process.env.NEXT_PUBLIC_DOMAIN || ''
    const imageUrl = data?.image ? baseUrl + data.image : ''

    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: data?.title?.[currentLocale],
      description: data?.description?.[currentLocale],
      image: imageUrl,
      sku: data?.id,
      offers: {
        '@type': 'Offer',
        url: `${baseUrl}/${currentLocale}/product/${data?.slug}`,
        priceCurrency: data?.currency || 'UZS',
        price: String(data?.out_price || ''),
        availability: 'https://schema.org/InStock',
      },
    }

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: baseUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: data?.title?.[currentLocale],
          item: `${baseUrl}/${currentLocale}/product/${data?.slug}`,
        },
      ],
    }

    return [productSchema, breadcrumbSchema]
  }, [data, currentLocale])

  return (
    <>
      <Box
        flex={1}
        display='flex'
        flexDirection='column'
        minHeight='0'
        color='text'
      >
        <IconButton
          zIndex={99}
          top={2}
          left={2}
          onClick={() => (onSuccess ? onSuccess() : router.back())}
          position={'fixed'}
          variant={'subtle'}
          rounded={'full'}
          bg='background.card'
          color='text'
        >
          <BackIcon color='currentColor' />
        </IconButton>
        <Slider list={[data?.image, ...(data?.gallery || [])]} />

        <Box px={4}>
          <Stack gap={0} w='full' pt={6}>
            {!hide_product_reviews && <Rating product={data} />}
            {(data?.weight || 0) > 0 && (
              <Text
                as={'span'}
                display={{ base: 'block', lg: 'none' }}
                fontSize='xs'
                fontWeight={400}
                lineHeight={1.4}
              >
                {data?.weight} {data?.measurement || 'г'}
              </Text>
            )}
          </Stack>
          <Text py={2} as={'h1'} fontSize={'20px'} fontWeight={600}>
            {data?.title?.[currentLocale]}
          </Text>

          <Description data={data} />
          <Nutrients data={data} />
          <Ingredients data={data} />
          {data?.allergens?.[currentLocale] && (
            <Alert
              mb={4}
              rounded={12}
              status='warning'
              title={data?.allergens?.[currentLocale]}
            />
          )}
        </Box>
        {data?.type === 'combo' ? (
          <Combo
            data={data}
            onSuccess={onSuccess}
            onRedirectToCart={onRedirectToCart}
          />
        ) : (
          <Details
            data={data}
            onSuccess={onSuccess}
            onRedirectToCart={onRedirectToCart}
          />
        )}
      </Box>

      {jsonLd && (
        <>
          {jsonLd.map((schema, i) => (
            <Script
              defer
              key={i}
              id={`${i}-json-ld`}
              strategy='lazyOnload'
              type='application/ld+json'
              dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
            />
          ))}
        </>
      )}
    </>
  )
}

export default ProductMobile
